import { SNA_IMAGE_PREFIX } from '@services/utils'
import { MapPin, Tag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { OfferList } from 'types/api'
import type { BaseResponse } from 'types/utils'

import { cn } from '@/lib/utils'

import {
  Badge,
  CardLink,
  CardSkeleton,
  DashboardCard,
  EmptyState,
  useFormatDate
} from './shared'

interface Props {
  offers?: BaseResponse<OfferList>
  loading?: boolean
  className?: string
}

const ACTION_STATUSES = ['CREATED', 'INTERESTED']

function offerTone(status?: string) {
  if (status && ['ACCEPTED', 'RESERVED', 'ONGOING'].includes(status))
    return 'teal'
  if (status && ACTION_STATUSES.includes(status)) return 'amber'
  return 'gray'
}

export function OffersCard({ offers, loading, className }: Props) {
  const { t } = useTranslation('userDashboard')
  const { t: tOffers } = useTranslation('offers')
  const formatDate = useFormatDate()

  const results = offers?.results ?? []
  const count = offers?.count ?? results.length
  const actionNeeded = results.filter(
    (o) => o.status && ACTION_STATUSES.includes(o.status)
  ).length
  const recent = results.slice(0, 3)

  return (
    <DashboardCard
      icon={Tag}
      title={t('offers.title')}
      className={className}
      action={
        count > 0 ? (
          <CardLink to="/user/offer">{t('offers.viewAll')}</CardLink>
        ) : undefined
      }
    >
      {loading ? (
        <CardSkeleton />
      ) : count === 0 ? (
        <EmptyState text={t('offers.empty')} />
      ) : (
        <div>
          <div className="mb-1 flex items-center gap-3">
            <span className="text-3xl font-bold text-ink">{count}</span>
            <span className="text-sm text-muted-foreground">
              {t('offers.count', { count })}
            </span>
            {actionNeeded > 0 && (
              <Badge tone="amber">{t('offers.actionNeeded')}</Badge>
            )}
          </div>

          <div className="divide-y divide-ink/[0.06]">
            {recent.map((offer) => {
              const flat = offer.flat
              return (
                <Link
                  key={offer.id}
                  to={`/user/offer/${offer.id}`}
                  className="group flex items-center gap-3 py-3"
                >
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-ink/[0.04]">
                    <img
                      src={`${SNA_IMAGE_PREFIX}${flat?.main_image}`}
                      alt={flat?.title ?? 'Flat'}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.style.visibility = 'hidden'
                      }}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-ink transition-colors group-hover:text-primary">
                      {flat?.title ?? '—'}
                    </div>
                    {flat?.location_name && (
                      <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0 text-primary" />
                        {flat.location_name}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="text-sm font-semibold text-ink">
                      {offer.monthly_rent_sum ?? offer.rent} €
                    </span>
                    {offer.status && (
                      <Badge tone={offerTone(offer.status)}>
                        {tOffers(`status.${offer.status.toLowerCase()}`, {
                          defaultValue: offer.status
                        })}
                      </Badge>
                    )}
                  </div>
                </Link>
              )
            })}
          </div>

          {recent.some(
            (o) =>
              o.deadline && o.status && ACTION_STATUSES.includes(o.status)
          ) && (
            <p
              className={cn(
                'mt-3 text-sm font-medium text-warning'
              )}
            >
              {t('offers.deadline', {
                date: formatDate(
                  recent.find(
                    (o) => o.deadline && o.status && ACTION_STATUSES.includes(o.status)
                  )?.deadline
                )
              })}
            </p>
          )}
        </div>
      )}
    </DashboardCard>
  )
}
